/**
 * Tableau. Quand `display.drill_dimensions` est renseigné, cliquer une ligne descend
 * d'un niveau (cf. `useDrillDown`) : le tableau n'affiche plus que la dimension
 * descendue et la première métrique. Sans `drill_dimensions`, les lignes sont inertes.
 */
import { Fragment, useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import type { WidgetConfig } from '../schemas';
import { formatDateLabel } from '../chartFormat';
import {
  useFieldCatalog,
  fieldLabel,
  metricLabel,
  metricFormat,
  metricGoodDirection,
  metricDef,
  type FieldCatalog,
} from '../fieldCatalog';
import { formatMetricValue } from '../formatValue';
import { useDrillDown } from '../useDrillDown';
import { DrillBreadcrumb } from '../DrillBreadcrumb';
import { computeCompare } from '../compareFormat';
import { TrendIndicator } from '../../../components/ui/TrendIndicator';
import { indexRowsByDimensions, dimensionKey } from '../tableCompare';

type Row = Record<string, string | number>;

/** Type d'agrégation d'une métrique : `additive` (se somme) ou `ratio` (jamais sommé tel quel). */
export function metricType(catalog: FieldCatalog | null, col: string): string | undefined {
  return metricDef(catalog, col)?.type;
}

/**
 * Valeur de la ligne de total pour une colonne métrique.
 * - additive → somme des lignes, formatée.
 * - ratio (ou type inconnu/absent) → `—` : un faux total serait pire que l'absence de total.
 */
export function computeTotalCell(
  col: string,
  rows: Row[],
  catalog: FieldCatalog | null,
): string {
  if (metricType(catalog, col) !== 'additive') return '—';
  const sum = rows.reduce((acc, row) => {
    const v = row[col];
    const n = typeof v === 'number' ? v : Number(v);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
  return formatMetricValue(sum, metricFormat(catalog, col));
}

/** Vrai s'il existe au moins une métrique additive parmi `metrics` — sinon la ligne de total est inutile. */
export function hasAdditiveMetric(metrics: string[], catalog: FieldCatalog | null): boolean {
  return metrics.some((m) => metricType(catalog, m) === 'additive');
}

interface WidgetProps {
  config: WidgetConfig;
  data: { current: Row[]; previous: Row[] | null; meta: any; subRows?: Row[] | null; subRowsError?: string | null } | null;
  loading?: boolean;
  error?: string;
}

const DEFAULT_LIMIT = 50;

export default function TableWidget({ config, data, loading, error }: WidgetProps) {
  // Hooks avant tout early return (règles des hooks) : libellés FR des entêtes + format des cellules.
  const catalog = useFieldCatalog(data?.meta?.source_type as string | undefined);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const display = (config.display ?? {}) as Record<string, unknown>;
  const drillDimensions = (display.drill_dimensions as string[] | undefined) ?? [];
  const firstMetric = config.metrics?.[0];
  const firstDimension = config.dimensions?.[0];
  const period = (data?.meta as { period?: { start: string; end: string } } | undefined)?.period;

  const drill = useDrillDown({
    enabled:
      drillDimensions.length > 0 &&
      !!config.data_source_id &&
      !!firstMetric &&
      !!firstDimension &&
      !!period,
    dimension: firstDimension ?? '',
    drillDimensions,
    dataSourceId: config.data_source_id ?? '',
    metric: firstMetric ?? '',
    topN: (display.limit as number | undefined) ?? DEFAULT_LIMIT,
    period: period ?? { start: '', end: '' },
  });

  // Un échec de drill-down (400, timeout…) s'affiche dans l'état d'erreur du widget.
  const shownError = error ?? drill.error;

  if (loading) {
    return (
      <div className="h-full">
        <div className="h-4 w-1/3 bg-[#F4F5FA] rounded animate-pulse mb-4" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-4 w-full bg-[#F4F5FA] rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (shownError) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-sm text-red-600">{shownError}</span>
      </div>
    );
  }

  const metrics = config.metrics ?? [];
  const dimensions = config.dimensions ?? [];

  const rowGroupDimension = (display.row_group_dimension as string | undefined) || undefined;
  const rowGroupMetricsList = (display.row_group_metrics as string[] | undefined) ?? [];

  if (!data || !config.data_source_id || !metrics.length) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  // Niveau descendu : une colonne de dimension (celle du dernier cran) + la première métrique.
  const isDrilled = drill.drillPath.length > 0 && !!drill.drillItems;
  const drilledDimension = drillDimensions[drill.drillPath.length - 1];

  const isRowGrouped = !!rowGroupDimension && !isDrilled && !!firstDimension;
  const rowGroupMetricSet = new Set(rowGroupMetricsList);

  /** Sous-lignes groupées par valeur de la dimension primaire. */
  function subRowsFor(row: Row): Row[] {
    if (!isRowGrouped || !data?.subRows) return [];
    const key = String(row[firstDimension!] ?? '');
    return data.subRows.filter((r) => String(r[firstDimension!] ?? '') === key);
  }

  function toggleExpanded(key: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const columns = isDrilled
    ? [drilledDimension, metrics[0]]
    : [...dimensions, ...metrics];
  const allRows: Row[] = isDrilled
    ? (drill.drillItems ?? []).map((item) => ({
        [drilledDimension]: item.key,
        [metrics[0]]: item.value,
      }))
    : data.current;

  // « Nombre de lignes » (`display.limit`) : au niveau descendu, le top-N est déjà appliqué
  // côté serveur (`topN` passé à useDrillDown) ; au niveau 0, c'est ICI qu'il doit mordre,
  // sinon le champ serait purement décoratif. Clé absente = pas de troncature.
  const limit = display.limit as number | undefined;
  const rows: Row[] =
    !isDrilled && typeof limit === 'number' && limit > 0 ? allRows.slice(0, limit) : allRows;

  const metricSet = new Set(metrics);
  const timeDimensions = new Set(
    dimensions.filter((dim) => catalog?.dimensions.find((d) => d.key === dim)?.time),
  );

  function formatCell(col: string, value: string | number | undefined): string {
    if (value === undefined || value === null) return '';
    if (metricSet.has(col)) return formatMetricValue(value, metricFormat(catalog, col));
    // Les buckets temporels arrivent bruts des connecteurs (`20260713`) : illisibles tels quels.
    if (timeDimensions.has(col)) return formatDateLabel(String(value), col);
    return String(value);
  }

  // Comparaison inline : indexe `previous` par dimensions, uniquement au niveau non-drillé.
  const prevIndex = !isDrilled && data.previous ? indexRowsByDimensions(data.previous, dimensions) : null;

  function renderMetricCell(col: string, row: Row) {
    const value = formatCell(col, row[col]);
    if (!prevIndex) return value;
    const key = dimensionKey(row, dimensions);
    const prevRow = prevIndex.get(key);
    const format = config.metric_compare_format?.[col] ?? 'percent';
    const parts = computeCompare(Number(row[col]), prevRow != null ? Number(prevRow[col]) : null, format);
    const fmt = metricFormat(catalog, col);
    const good = metricGoodDirection(catalog, col, config.metric_good_direction?.[col]);
    // Aligner les items du bloc (valeur + delta) sur l'alignement de la colonne,
    // sinon la valeur suit `text-right` de la cellule mais le TrendIndicator reste à gauche.
    const itemsAlign =
      metricAlign === 'left' ? 'items-start' : metricAlign === 'center' ? 'items-center' : 'items-end';
    return (
      <div className={`flex flex-col gap-0.5 ${itemsAlign}`}>
        <span>{value}</span>
        {parts.showDelta && parts.kind === 'percent' && parts.percent !== null && (
          <TrendIndicator value={parts.percent} goodDirection={good} />
        )}
        {parts.showDelta && parts.kind === 'diff' && parts.diff !== null && (
          <TrendIndicator value={parts.diff} formattedValue={`${parts.diff > 0 ? '+' : ''}${formatMetricValue(parts.diff, fmt)}`} goodDirection={good} />
        )}
        {parts.showDelta && parts.kind === 'both' && parts.diff !== null && (
          <TrendIndicator value={parts.percent ?? parts.diff} formattedValue={`${parts.diff > 0 ? '+' : ''}${formatMetricValue(parts.diff, fmt)}${parts.percent !== null ? ` (${parts.percent > 0 ? '+' : ''}${parts.percent}%)` : ''}`} goodDirection={good} />
        )}
      </div>
    );
  }

  // Dimension pilotant le clic : celle du niveau courant (widget au niveau 0, drill ensuite).
  const clickDimension = isDrilled ? drilledDimension : firstDimension;

  function handleRowClick(row: Row) {
    if (!drill.canDrillDown || !clickDimension) return;
    void drill.handleDrillDown(String(row[clickDimension] ?? ''));
  }

  // ── Options de style (StyleTab, onglet Tableau) ──
  const boldHeader = (display.bold_header as boolean | undefined) ?? true;
  const boldTotal = (display.bold_total as boolean | undefined) ?? true;
  const boldFirstCol = (display.bold_first_col as boolean | undefined) ?? false;
  const striped = (display.striped as boolean | undefined) ?? false;
  const density = (display.density as 'compact' | 'normal' | undefined) ?? 'normal';
  const metricAlign = (display.metric_align as 'left' | 'center' | 'right' | undefined) ?? 'right';
  const showTotal = (display.show_total as boolean | undefined) ?? false;

  const cellPadding = density === 'compact' ? 'py-1 px-2' : 'py-2 px-2';
  const alignClass = (col: string) =>
    metricSet.has(col)
      ? metricAlign === 'left'
        ? 'text-left'
        : metricAlign === 'center'
          ? 'text-center'
          : 'text-right'
      : 'text-left';

  // Ligne de total : uniquement au niveau non-drillé, et seulement si au moins une métrique
  // additive existe (sinon la ligne serait une rangée de tirets, sans intérêt).
  const showTotalRow = showTotal && !isDrilled && hasAdditiveMetric(metrics, catalog);

  return (
    <div className="h-full overflow-auto">
      {config.title && <div className="text-sm font-medium text-[#1B2559] mb-3">{config.title}</div>}
      <DrillBreadcrumb drillPath={drill.drillPath} catalog={catalog} onDrillTo={drill.drillTo} />
      {drill.isLoading ? (
        <div className="text-sm text-[#8B8FA7]">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-[#8B8FA7] italic">Aucune donnée</div>
      ) : (
        <>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-[#E2E4ED]">
              {columns.map((col, colIdx) => (
                <th
                  key={col}
                  className={`${alignClass(col)} ${cellPadding} text-xs uppercase tracking-wide ${
                    boldHeader ? 'font-semibold' : 'font-normal'
                  } ${colIdx === 0 && boldFirstCol ? 'font-semibold' : ''} text-[#8B8FA7]`}
                >
                  {config.metrics?.includes(col)
                    ? metricLabel(catalog, col, config.metric_labels)
                    : fieldLabel(catalog, col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const parentKey = firstDimension ? String(row[firstDimension] ?? '') : String(idx);
              const children = subRowsFor(row);
              const expanded = expandedKeys.has(parentKey);
              return (
                <Fragment key={idx}>
                  <tr
                    onClick={() => handleRowClick(row)}
                    className={`border-b border-[#E2E4ED] last:border-b-0 ${
                      drill.canDrillDown ? 'cursor-pointer hover:bg-[#F4F5FA]' : ''
                    } ${striped && idx % 2 === 1 ? 'bg-[#F9FAFB]' : ''}`}
                  >
                    {columns.map((col, colIdx) => (
                      <td
                        key={col}
                        className={`${cellPadding} ${alignClass(col)} text-[#1B2559] ${
                          colIdx === 0 && boldFirstCol ? 'font-semibold' : ''
                        }`}
                      >
                        {colIdx === 0 && isRowGrouped && children.length > 0 && (
                          <button
                            type="button"
                            data-widget-interactive
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(parentKey); }}
                            aria-label={`${expanded ? 'Replier' : 'Déplier'} ${parentKey}`}
                            className="mr-1 inline-flex align-middle text-[#8B8FA7] hover:text-[#1B2559]"
                          >
                            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        )}
                        {metricSet.has(col) ? renderMetricCell(col, row) : formatCell(col, row[col])}
                      </td>
                    ))}
                  </tr>
                  {expanded && children.map((child, cIdx) => (
                    <tr key={`${idx}-sub-${cIdx}`} className="border-b border-[#E2E4ED] last:border-b-0 bg-[#FAFAFB]">
                      {columns.map((col, colIdx) => (
                        <td key={col} className={`${cellPadding} ${alignClass(col)} text-[#1B2559]`}>
                          {colIdx === 0
                            ? <span className="pl-5 text-[#5F6477]">{formatCell(rowGroupDimension!, child[rowGroupDimension!])}</span>
                            : rowGroupMetricSet.has(col)
                              ? formatCell(col, child[col])
                              : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
          {showTotalRow && (
            <tfoot>
              <tr className="border-t-2 border-[#E2E4ED]">
                {columns.map((col, colIdx) => {
                  const isFirstCol = colIdx === 0;
                  const isMetric = metricSet.has(col);
                  let content: string = '';
                  if (isFirstCol) content = 'Total';
                  else if (isMetric) content = computeTotalCell(col, rows, catalog);
                  return (
                    <td
                      key={col}
                      className={`${cellPadding} ${alignClass(col)} text-[#1B2559] ${
                        boldTotal ? 'font-semibold' : ''
                      }`}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
        {isRowGrouped && data?.subRowsError && (
          <p className="mt-2 text-xs text-[#8B8FA7] italic">
            Détail par {fieldLabel(catalog, rowGroupDimension!)} non disponible.
          </p>
        )}
        </>
      )}
    </div>
  );
}
